import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/errors/use-toast';
import { deleteTask, fetchTaskById } from '../database-tasks';
import { updateGoalCompletionStatus } from '@/hooks/goal-planner/database-goals';
import { useUser } from '@/hooks/auth/use-user';
import { mapDbTaskToUiTask } from '../types';

/**
 * Custom hook for removing a task
 */
export const useDeleteTaskMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  
  return useMutation({
    mutationFn: async (taskId: string) => {
      // First, fetch the task to get its goalId before deletion
      const { data: taskData, error: fetchError } = await fetchTaskById(taskId);
      if (fetchError) throw new Error(fetchError.message);
      
      const task = taskData ? mapDbTaskToUiTask(taskData) : null;
      
      // Delete the task
      const { error } = await deleteTask(taskId);
      if (error) throw new Error(error.message);
      
      return { taskId, goalId: task?.goalId };
    },
    onSuccess: async ({ taskId, goalId }) => {
      // Invalidate project-specific task queries
      queryClient.invalidateQueries({ 
        queryKey: ['tasks', userId],
        exact: false 
      });
      
      // Invalidate TaskManager-specific queries for any users who might have been assigned
      if (user?.id) {
        queryClient.invalidateQueries({ 
          queryKey: ['task-manager-tasks', user.id],
          exact: false 
        });
        queryClient.invalidateQueries({ 
          queryKey: ['assigned-tasks-count', user.id],
          exact: false 
        });
        
        // Since we don't know who was assigned, we'll invalidate broader patterns
        // This ensures the navbar count updates for any user who was assigned
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return (
              (Array.isArray(key) && key[0] === 'task-manager-tasks') ||
              (Array.isArray(key) && key[0] === 'assigned-tasks-count')
            );
          }
        });
      }
      
      // Update goal completion status if task was linked to a goal
      if (goalId && user?.id) {
        try {
          await updateGoalCompletionStatus(goalId, user.id);
          // Invalidate goal queries to reflect updated completion status
          queryClient.invalidateQueries({ 
            queryKey: ['goals', user.id],
            exact: false 
          });
        } catch (error) {
          console.error('Failed to update goal completion status:', error);
          // Don't fail the whole operation for this
        }
      }
      
      toast({
        title: "Task removed",
        description: "The task has been removed successfully",
        variant: "default",
      });
    },
    onError: (error) => {
      toast({
        title: "Error removing task",
        description: error.message || "Failed to remove task",
        variant: "error",
      });
    }
  });
};
